// frontend/src/components/UsageNotificationSnackbar.js
// Snackbar component for displaying usage notifications

import React from 'react';
import {
  Snackbar,
  Alert,
  Button,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useUsageNotifications from '../hooks/useUsageNotifications';

const UsageNotificationSnackbar = () => {
  const navigate = useNavigate();
  const { activeNotification, dismissNotification } = useUsageNotifications();

  if (!activeNotification) {
    return null;
  }

  const { type, title, message, action } = activeNotification;

  const handleActionClick = () => {
    if (action?.url) {
      navigate(action.url);
    }
    dismissNotification();
  };

  const getSeverity = () => {
    switch (type) {
      case 'warning': return 'warning';
      case 'error': return 'error';
      case 'success': return 'success';
      case 'info': 
      default: return 'info';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'warning': return <WarningIcon />;
      case 'error': return <ErrorIcon />;
      case 'success': return <SuccessIcon />;
      case 'info': 
      default: return <InfoIcon />;
    }
  };

  return (
    <Snackbar
      open={true}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{ mt: 8 }} // Offset from header
    >
      <Alert
        severity={getSeverity()}
        icon={getIcon()}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {action && (
              <Button
                size="small"
                variant="contained"
                onClick={handleActionClick}
                sx={{
                  minWidth: 'auto',
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                {action.label}
              </Button>
            )}
            <IconButton
              size="small"
              onClick={dismissNotification}
              sx={{ color: 'inherit' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
        sx={{
          minWidth: 350,
          maxWidth: 500,
          '& .MuiAlert-message': {
            flex: 1,
            minWidth: 0
          }
        }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="body2">
            {message}
          </Typography>
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default UsageNotificationSnackbar;